import React, { useEffect, useState } from 'react';
import Input from '../../../components/Input';
import Button from '../../../components/Button';
import { IoSearchOutline } from 'react-icons/io5';
import { HiOutlineArrowLeft } from 'react-icons/hi';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import { FaAngleLeft, FaAngleRight } from 'react-icons/fa';
import Super_admin_web_protal from '../../../layout/Super_admin_web_portal';
import ViewSlip from '../InvestorsDetails/ViewSlip';
import { Base_url } from '../../../utils/Base_url';
import BioDataModal from '../../../components/BioDataModal';

const ClosedInvestorDeposits = () => {
  const navigate = useNavigate();
  const { cnic, enrollmentId } = useParams();
  const [deposits, setDeposits] = useState([]);
  const [investorData, setInvestorData] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [enrollmentData, setEnrollmentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredDeposits, setFilteredDeposits] = useState([]);
  const [getData, setGetData] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBioModalOpen, setIsBioModalOpen] = useState(false);

  const itemsPerPage = 6;
  const [currentPage, setCurrentPage] = useState(1);
  const currentPageData = filteredDeposits.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const handlePageChange = (newPage) => setCurrentPage(newPage);

  // Fetch enrollment details first, then fetch closed deposits
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Step 1: Fetch enrollment details to get planId
        const enrollmentRes = await fetch(
          `${Base_url}/v1/worker/enrollement-by-cnic?cnic=${cnic}`
        );
        const enrollmentResult = await enrollmentRes.json();

        let planId = null;
        let enrollment = null;

        if (enrollmentResult.success && enrollmentResult.enrollments) {
          enrollment = enrollmentResult.enrollments.find(
            (e) => e._id === enrollmentId
          );
          setEnrollmentData(enrollment);

          // Get planId from enrollment
          planId = enrollment?.planId?._id || enrollment?.planId;
        }

        if (!planId) {
          setError('Could not find plan information for this enrollment.');
          setLoading(false);
          return;
        }

        // Step 2: Fetch closed deposits using the new API
        const myHeaders = new Headers();
        myHeaders.append("Content-Type", "application/json");

        const raw = JSON.stringify({
          cnic: cnic,
          planId: planId
        });

        const requestOptions = {
          method: "POST",
          headers: myHeaders,
          body: raw,
          redirect: "follow"
        };

        const depositsRes = await fetch(
          `${Base_url}/v1/worker/closed-deposit-by-investor-plan`,
          requestOptions
        );
        const depositsData = await depositsRes.json();

        if (depositsData.success) {
          setDeposits(depositsData.deposits || []);
          setFilteredDeposits(depositsData.deposits || []);
          setInvestorData(depositsData.investor || null);
          setSummaryData(depositsData.summary || null);
        } else {
          setDeposits([]);
          setFilteredDeposits([]);
        }

      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (cnic && enrollmentId) {
      fetchData();
    }
  }, [cnic, enrollmentId]);

  // Filter deposits based on search
  useEffect(() => {
    let filtered = deposits || [];

    if (searchTerm.trim()) {
      const lowercasedSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter((val) => {
        const idMatch = val?._id?.toLowerCase().includes(lowercasedSearchTerm);
        const amountMatch = String(val?.amount).toLowerCase().includes(lowercasedSearchTerm);
        const typeMatch = val?.depositType?.toLowerCase().includes(lowercasedSearchTerm);
        const nameMatch = val?.name?.toLowerCase().includes(lowercasedSearchTerm);
        const referenceMatch = val?.reference?.toLowerCase().includes(lowercasedSearchTerm);
        return idMatch || amountMatch || typeMatch || nameMatch || referenceMatch;
      });
    }

    setFilteredDeposits(filtered);
    setCurrentPage(1);
  }, [deposits, searchTerm]);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PK', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const investorName = localStorage.getItem('iName') || investorData?.fullName || 'Investor';

  return (
    <Super_admin_web_protal
      language={<p className="sm:block hidden">English | Urdu</p>}
      ToggleBtn={
        <div className="flex gap-4 items-center">
          <HiOutlineArrowLeft
            className="cursor-pointer hover:text-primary transition-colors"
            onClick={() => navigate('/super_admin/completed')}
          />
          <h2 className="">
            Closed Enrollments{' '}
            <span className="text-darkGray font-medium text-sm">/ {investorName}</span>
          </h2>
        </div>
      }
      title_props={
        <div className="w-96 relative">
          <Input
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search deposits..."
            className={'border bg-white w-full rounded-full'}
          />
          <Button
            Icon={<IoSearchOutline className="text-white text-xl" />}
            label={'search'}
            className={
              'absolute top-0 right-0 bg-secondary text-white uppercase font-normal rounded-full'
            }
          />
        </div>
      }
    >
      <ViewSlip
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        getData={getData}
      />

      {/* Header with back button and info */}
      <div className="sm:flex block justify-between items-center mb-6">
        <div>
          <h2 className="sm:block hidden text-xl font-semibold">
            Closed Enrollment Deposits{' '}
            <span className="text-darkGray font-medium text-sm">/ {investorName}</span>
          </h2>
          <button
            onClick={() => navigate('/super_admin/completed')}
            className="flex items-center gap-2 text-secondary hover:text-primary transition-colors mt-2"
          >
            <HiOutlineArrowLeft />
            <span>Back to Closed Enrollments</span>
          </button>
        </div>

        <div className="flex gap-3 items-center mt-4 sm:mt-0">
          {summaryData && (
            <div className="bg-white border border-secondary rounded-xl px-4 py-2">
              <span className="text-[#030229B2] font-medium">
                Total Deposits: Rs. {formatCurrency(summaryData.totalClosedDepositAmount || 0)}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Summary Card from API */}
      {summaryData && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow-md p-6 mb-6 border border-blue-100">
          <h3 className="text-lg font-semibold mb-4 text-gray-800">Deposit Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white p-3 rounded-lg shadow-sm">
              <p className="text-sm text-gray-500">Closed Deposits Count</p>
              <p className="font-bold text-2xl text-gray-800">{summaryData.closedDepositsCount || 0}</p>
            </div>
            <div className="bg-white p-3 rounded-lg shadow-sm">
              <p className="text-sm text-gray-500">Total Closed Amount</p>
              <p className="font-bold text-xl text-green-600">Rs. {formatCurrency(summaryData.totalClosedDepositAmount || 0)}</p>
            </div>
            <div className="bg-white p-3 rounded-lg shadow-sm">
              <p className="text-sm text-gray-500">Total All Deposits</p>
              <p className="font-bold text-2xl text-gray-800">{summaryData.totalAllDeposits || 0}</p>
            </div>
            <div className="bg-white p-3 rounded-lg shadow-sm">
              <p className="text-sm text-gray-500">Percentage Closed</p>
              <p className="font-bold text-xl text-blue-600">{summaryData.percentageClosed || '0%'}</p>
            </div>
          </div>
          {summaryData.message && (
            <div className="mt-4 pt-4 border-t border-blue-200">
              <p className="text-sm text-blue-700 font-medium">{summaryData.message}</p>
            </div>
          )}
        </div>
      )}

      {/* Investor Info Card */}
      {investorData && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Investor Information</h3>
            <button
              type="button"
              onClick={() => setIsBioModalOpen(true)}
              disabled={!investorData?._id}
              className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md text-white bg-secondary hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              View Bio Data Form
            </button>
          </div>
          <div className="flex flex-col md:flex-row gap-6">
            {investorData.images && (
              <div className="flex-shrink-0">
                <img
                  src={investorData.images}
                  alt={investorData.fullName}
                  className="w-24 h-24 rounded-full object-cover border-4 border-gray-100 shadow"
                />
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 flex-grow">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-500">Full Name</p>
                <p className="font-medium text-gray-800">{investorData.fullName}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-500">CNIC</p>
                <p className="font-medium text-gray-800">{investorData.cnic}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-500">Contact</p>
                <p className="font-medium text-gray-800">{investorData.contactInfo || 'N/A'}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enrollment Summary Card */}
      {enrollmentData && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-800">Enrollment Details</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm text-gray-500">Plan Name</p>
              <p className="font-medium text-gray-800">{enrollmentData.planId?.planName || enrollmentData.planName || 'N/A'}</p>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm text-gray-500">Years</p>
              <p className="font-medium text-gray-800">{enrollmentData.numberOfYears} years</p>
            </div>
            <div className="bg-green-50 p-3 rounded-lg">
              <p className="text-sm text-green-600">Total Deposited</p>
              <p className="font-medium text-green-800">Rs. {formatCurrency(enrollmentData.totalDepositedAmount || 0)}</p>
            </div>
            <div className="bg-green-50 p-3 rounded-lg">
              <p className="text-sm text-green-600">Total Profit</p>
              <p className="font-medium text-green-800">Rs. {formatCurrency(enrollmentData.totalProfit || 0)}</p>
            </div>
            <div className="bg-red-50 p-3 rounded-lg">
              <p className="text-sm text-red-600">Total Loss</p>
              <p className="font-medium text-red-800">Rs. {formatCurrency(enrollmentData.totalLoss || 0)}</p>
            </div>
            <div className="bg-red-50 p-3 rounded-lg">
              <p className="text-sm text-red-600">Total Deduction</p>
              <p className="font-medium text-red-800">Rs. {formatCurrency(enrollmentData.totalDeduction || 0)}</p>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm text-gray-500">Status</p>
              <span className={`inline-block mt-1 px-3 py-1 rounded-full text-sm font-medium ${enrollmentData.status === 'closed'
                  ? 'bg-gray-200 text-gray-800'
                  : 'bg-green-100 text-green-800'
                }`}>
                {enrollmentData.status === 'closed' ? 'Closed' : 'Active'}
              </span>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm text-gray-500">Payment Status</p>
              <span className={`inline-block mt-1 px-3 py-1 rounded-full text-sm font-medium ${
                (enrollmentData.paymentStatus || 'pending') === 'pending'
                  ? 'bg-amber-100 text-amber-800'
                  : (enrollmentData.paymentStatus || '').toLowerCase() === 'paid'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-800'
              }`}>
                {(enrollmentData.paymentStatus || 'pending')}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-20">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-secondary border-t-transparent mb-4"></div>
            <p className="text-gray-600">Loading deposit data...</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="text-center py-20">
          <p className="text-xl text-red-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-secondary hover:bg-primary text-white rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Deposits Table */}
      {!loading && !error && (
        <section className="mb-20 text-gray-800">
          <div className="block rounded-lg bg-white shadow">
            <div className="flex flex-col overflow-hidden">
              <div className="overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div className="inline-block min-w-full sm:px-6 lg:px-8">
                  <div className="">
                    <table className="min-w-full mb-0">
                      <thead className="bg-gray-50">
                        <tr className="rounded-lg whitespace-nowrap">
                          <th scope="col" className="text-sm text-darkGray font-medium px-6 py-4">
                            Receipt No
                          </th>
                          <th scope="col" className="text-sm text-darkGray font-medium px-6 py-4">
                            Deposit Date
                          </th>
                          <th scope="col" className="text-sm text-darkGray font-medium px-6 py-4">
                            Deposit Type
                          </th>
                          <th scope="col" className="text-sm text-darkGray font-medium px-6 py-4">
                            Amount
                          </th>
                          <th scope="col" className="text-sm text-darkGray font-medium px-6 py-4">
                            Bank/Branch
                          </th>
                          <th scope="col" className="text-sm text-darkGray font-medium px-6 py-4">
                            Reference
                          </th>
                          <th scope="col" className="text-sm text-darkGray font-medium px-6 py-4">
                            Acknowledged By
                          </th>
                          <th scope="col" className="text-sm text-darkGray font-medium px-6 py-4">
                            Status
                          </th>
                          <th scope="col" className="text-sm text-darkGray font-medium px-6 py-4">
                            Slip
                          </th>
                        </tr>
                      </thead>
                      <tbody className="">
                        {currentPageData?.length > 0 ? (
                          currentPageData.map((item, index) => (
                            <tr key={item._id || index} className="bg-white border-8 border-[#F6F6F6] rounded-md hover:bg-gray-50 transition-colors">
                              <td className="align-middle text-sm font-normal px-6 py-4 whitespace-nowrap text-center">
                                <span className="text-base text-black py-1 px-2.5 leading-none text-center whitespace-nowrap align-baseline bg-blue-100 rounded-full">
                                  {item?.receiptNumber || 'N/A'}
                                </span>
                              </td>
                              <td className="text-sm font-normal text-center px-6 py-4 whitespace-nowrap">
                                <span className="text-base text-black py-1 px-2.5 leading-none text-center whitespace-nowrap align-baseline bg-green-200 rounded-full">
                                  {moment(item?.depositDate).format('DD/MM/YYYY')}
                                </span>
                              </td>
                              <td className="align-middle text-center text-sm font-normal px-6 py-4 whitespace-nowrap">
                                <span className="text-base text-black py-1 px-2.5 leading-none whitespace-nowrap bg-purple-100 rounded-full capitalize">
                                  {item?.depositType || 'N/A'}
                                </span>
                              </td>
                              <td className="align-middle text-center text-sm font-normal px-6 py-4 whitespace-nowrap">
                                <span className="text-base text-green-700 font-semibold py-1 px-2.5 leading-none whitespace-nowrap bg-green-100 rounded-full">
                                  Rs. {formatCurrency(item?.amount || 0)}
                                </span>
                              </td>
                              <td className="align-middle text-center text-sm font-normal px-6 py-4 whitespace-nowrap">
                                <div className="flex flex-col">
                                  <span className="text-sm text-black">{item?.bankName || '-'}</span>
                                  <span className="text-xs text-gray-500">{item?.branchName || ''}</span>
                                </div>
                              </td>
                              <td className="align-middle text-center text-sm font-normal px-6 py-4 whitespace-nowrap">
                                <span className="text-base text-black py-1 px-2.5 leading-none whitespace-nowrap bg-yellow-100 rounded-full">
                                  {item?.reference || '-'}
                                </span>
                              </td>
                              <td className="align-middle text-center text-sm font-normal px-6 py-4 whitespace-nowrap">
                                <div className="flex flex-col">
                                  <span className="text-sm text-black">{item?.acknowledgeAuth || '-'}</span>
                                  <span className="text-xs text-gray-500">{item?.acknowledgeDate || ''}</span>
                                </div>
                              </td>
                              <td className="align-middle text-center text-sm font-normal px-6 py-4 whitespace-nowrap">
                                <span className={`text-sm py-1 px-2.5 leading-none whitespace-nowrap rounded-full ${item?.depositStatus === 'closed'
                                    ? 'bg-gray-200 text-gray-700'
                                    : 'bg-green-100 text-green-700'
                                  }`}>
                                  {item?.depositStatus || 'N/A'}
                                </span>
                              </td>
                              <td className="align-middle cursor-pointer text-center text-sm font-normal px-6 py-4 whitespace-nowrap">
                                {item?.slip ? (
                                  <span
                                    onClick={() => {
                                      openModal();
                                      setGetData(item);
                                    }}
                                    className="text-base text-secondary py-1 px-2.5 leading-none whitespace-nowrap bg-green-200 rounded-full cursor-pointer hover:text-primary transition-colors"
                                  >
                                    View
                                  </span>
                                ) : (
                                  <span className="text-base text-gray-400 py-1 px-2.5">-</span>
                                )}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="9" className="text-center py-8">
                              <div className="flex flex-col items-center justify-center">
                                <p className="text-gray-500 text-lg mb-2">No closed deposits found</p>
                                <p className="text-gray-400 text-sm">
                                  {searchTerm ? 'Try a different search term' : 'No closed deposit records for this enrollment'}
                                </p>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Pagination */}
            {filteredDeposits.length > 0 && (
              <div className="flex flex-col sm:flex-row justify-between items-center py-4 px-6 border-t bg-gray-50">
                <div className="text-sm text-gray-600 mb-3 sm:mb-0">
                  Showing {Math.min((currentPage - 1) * itemsPerPage + 1, filteredDeposits.length)} to {Math.min(currentPage * itemsPerPage, filteredDeposits.length)} of {filteredDeposits.length} deposits
                </div>
                <div className="flex items-center gap-4">
                  <button
                    className={`${currentPage === 1
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-secondary text-white hover:bg-primary'
                      } flex justify-center items-center w-10 h-10 rounded-md transition-colors`}
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    <FaAngleLeft size={20} />
                  </button>
                  <span className="text-primary font-semibold">
                    {currentPage} of {Math.ceil(filteredDeposits.length / itemsPerPage) || 1}
                  </span>
                  <button
                    className={`${currentPage * itemsPerPage >= filteredDeposits.length
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-secondary text-white hover:bg-primary'
                      } flex justify-center items-center w-10 h-10 rounded-md transition-colors`}
                    disabled={currentPage * itemsPerPage >= filteredDeposits.length}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    <FaAngleRight size={20} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>
      )}
      <BioDataModal
        isOpen={isBioModalOpen}
        onClose={() => setIsBioModalOpen(false)}
        investorId={investorData?._id}
        readOnly
      />
    </Super_admin_web_protal>
  );
};

export default ClosedInvestorDeposits;
